import React from 'react';
import { createPortal } from 'react-dom';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Popover for inline table cell editors (status, priority, ...).
 *
 * Table cells sit inside scrolling/overflow containers, so an absolutely positioned
 * popup gets clipped and painted over by neighbouring rows. This portals to the body
 * and positions itself against the anchor's real bounding rect instead.
 */
const TablePopover = ({
    anchorRect,
    popupHeight = 120,
    popupWidth = 140,
    viewportPadding = 12,
    offset = 6,
    align = 'left',
    zIndex = 10000,
    innerRef,
    style,
    children,
}) => {
    if (typeof document === 'undefined') return null;

    const rect = anchorRect || {};
    const viewportHeight = document.documentElement.clientHeight;
    const viewportWidth = document.documentElement.clientWidth;

    const anchorTop = rect.top ?? rect.y ?? 0;
    const anchorBottom = rect.bottom ?? anchorTop;
    const anchorLeft = rect.left ?? rect.x ?? 0;
    const anchorRight = rect.right ?? anchorLeft;

    // Flip above the anchor when there is not enough room underneath it.
    const openBelow = anchorBottom + offset + popupHeight <= viewportHeight - viewportPadding;
    const top = openBelow
        ? anchorBottom + offset
        : Math.max(anchorTop - popupHeight - offset, viewportPadding);

    const preferredLeft = align === 'right' ? anchorRight - popupWidth : anchorLeft;
    const left = clamp(
        preferredLeft,
        viewportPadding,
        Math.max(viewportWidth - popupWidth - viewportPadding, viewportPadding)
    );

    return createPortal(
        <div
            ref={innerRef}
            style={{
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                zIndex,
                background: '#FAF9FB',
                border: '1px solid #D1D2E0',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                minWidth: '100px',
                ...style,
            }}
        >
            {children}
        </div>,
        document.body
    );
};

export default TablePopover;
